import Enumerable from "linq";
import {
  IHisInformationBean,
  IHisInformationItemBean,
  IHisInformationTableBean,
  IHisInformationTableRowBean,
  IHisInformationTableHeaderBean,
  IHisInformationSettingBean,
  IHisInformationVisitHistoryBean,
  IHisInformationVisitHistoryDetailBean,
  IHisInformationHospitalizationHistoryBean,
  IHisInformationHospitalizationHistoryDetailBean,
  IDepartmentRelatedHisInformationBean,
} from "./beans";
import {
  HisInformation,
  HisInformationItem,
  HisInformationSetting,
  VisitHistoryInformation,
  HospitalizationHistoryInformation,
  DepartmentRelatedHisInformation,
  HisInformationProcessResult,
  HisInformationErrorResult,
} from "../../../domain/his";
import { Table, Header, Row, Cell } from "../../../domain/system/table";
import { User } from "../../../domain/user";
import { Department } from "../../../domain/department";
import { IHisInformationProcessResultRean } from "./beans/IHisInformationProcessResultRean";
import { IHisInformationErrorResultRean } from "./beans/IHisInformationErrorResultRean";

export class HisInformationTranslator {
  public toHisInformation(bean: IHisInformationBean): HisInformation {
    if (!bean) {
      return new HisInformation(
        [],
        new Table([], []),
        new Table([], []),
        new Table([], []),
        [],
        [],
        [],
        null,
        bean,
      );
    }
    return new HisInformation(
      this.toHisInformationItems(bean.items),
      this.toTable(bean.disease),
      this.toTable(bean.hospitalization),
      this.toTable(bean.operation),
      this.toVisitHistories(bean.visitHistories),
      this.toHospitalizationHistories(bean.hospitalizationHistories),
      this.toDepartmentRelatedHisInformations(bean.departmentRelatedInformations),
      this.toProcessResult(bean.processResult),
      bean,
    );
  }

  private toDepartmentRelatedHisInformations(beans: IDepartmentRelatedHisInformationBean[]): DepartmentRelatedHisInformation[] {
    if (!beans) { return []; }
    return beans.map(bean => this.toDepartmentRelatedHisInformation(bean));
  }
  
  private toDepartmentRelatedHisInformation(bean: IDepartmentRelatedHisInformationBean): DepartmentRelatedHisInformation {
    const info = new DepartmentRelatedHisInformation();
    info.isDefault = !!bean.isDefault;
    info.departmentCodes = bean.departmentCodes ? bean.departmentCodes : [];
    info.items = this.toHisInformationItems(bean.items);
    return info;
  }

  private toHisInformationItems(beans: IHisInformationItemBean[]): HisInformationItem[] {
    if (!beans) { return []; }

    const items = beans.map(x => new HisInformationItem(x.keyText, x.value));
    return items;
  }

  private toTable(bean: IHisInformationTableBean): Table {
    if (!bean) { return new Table([], []); }

    const table = new Table(this.toHeaders(bean.headers), this.toRows(bean.rows));
    return table;
  }

  private toHeaders(beans: IHisInformationTableHeaderBean[]): Header[] {
    if (!beans) { return []; }

    const headers = beans.map(x => new Header(x.title, x.value, -1, !x.isInvisible));
    return headers;
  }

  private toRows(beans: IHisInformationTableRowBean[]): Row[] {
    if (!beans) { return []; }

    const rows = beans.map(x => new Row(this.toCells(x.values)));
    return rows;
  }

  private toCells(values: string[]): Cell[] {
    if (!values) { return []; }

    const cells = values.map(x => new Cell(x));
    return cells;
  }

  public toHisInformationSettings(beans: IHisInformationSettingBean[]): HisInformationSetting[] {
    return Enumerable.from(beans).select(bean => new HisInformationSetting(
      bean.key,
      bean.name,
      bean.itemType,
      bean.objectType,
      bean.edit,
      bean.isFreeInput,
      bean.isReplace,
      bean.dataType,
      bean.isMultiSelect,
      bean.screenType,
      bean.isWarning,
      bean.reloadType,
      bean.setPlaceType,
      bean.explanation,
      bean.calculate,
    )).toArray();
  }

  public toHisInformationBean(hisInformation: HisInformation): IHisInformationBean {
    // わざわざ変換しなおすメリットが無いので保持していたものをそのまま返却する
    return hisInformation.origin;
  }

  private toVisitHistories(beans: IHisInformationVisitHistoryBean[]): VisitHistoryInformation[] {
    if (!beans) { return []; }
    return beans.map(b => this.toVisitHistory(b));
  }

  private toVisitHistory(bean: IHisInformationVisitHistoryBean): VisitHistoryInformation {
    const info = new VisitHistoryInformation(bean.visitDate, this.toDepartment(bean.department), this.toUser(bean.doctor));
    Object.freeze(info);
    return info;
  }

  private toDepartment(bean: IHisInformationVisitHistoryDetailBean): Department {
    if (!bean) { return null; }
    // TODO: 診療科コードが正しいかどうかの検証をどこでやる？（不要？）
    return new Department(bean.code, bean.name, "");
  }

  private toUser(bean: IHisInformationVisitHistoryDetailBean): User {
    if (!bean) { return null; }
    // TODO: 職員コードが正しいかどうかの検証をどこでやる？（不要？）
    return new User(bean.code, bean.name, null, "", "", "");
  }

  private toHospitalizationHistories(beans: IHisInformationHospitalizationHistoryBean[]): HospitalizationHistoryInformation[] {
    if (!beans) { return []; }
    return beans.map(b => this.toHospitalizationHistory(b));
  }

  private toHospitalizationHistory(bean: IHisInformationHospitalizationHistoryBean): HospitalizationHistoryInformation {
    const info = new HospitalizationHistoryInformation(bean.admissionDate, bean.dischargeDate, this.toHospitalizationDepartment(bean.department), this.toHospitalizationUsers(bean.doctors));
    Object.freeze(info);
    return info;
  }

  private toHospitalizationDepartment(bean: IHisInformationHospitalizationHistoryDetailBean): Department {
    if (!bean) { return null; }
    return new Department(bean.code, bean.name, "");
  }

  private toHospitalizationUsers(beans: IHisInformationHospitalizationHistoryDetailBean[]): User[] {
    if (!beans) { return []; }
    return beans.map(b => this.toHospitalizationUser(b)); 
  }

  private toHospitalizationUser(bean: IHisInformationVisitHistoryDetailBean): User {
    if (!bean) { return null; }
    return new User(bean.code, bean.name, null, "", "", "");
  }

  private toProcessResult(bean: IHisInformationProcessResultRean): HisInformationProcessResult {
    if (!bean || !bean.errorResults) { return null; }
    const result = new HisInformationProcessResult();
    result.errorResults = bean.errorResults.map(r => this.toHisInformationErrorResult(r));
    return result;
  }

  private toHisInformationErrorResult(bean: IHisInformationErrorResultRean): HisInformationErrorResult {
    const result = new HisInformationErrorResult();
    result.name = bean.name;
    return result;
  }
}
